import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper, DetailInfo, DetailRow } from '@labrav/common'
import { useStyles } from '../ViewDetailsResellerAuthorization.styles'
import type { ViewResellerAuthorizationType } from '../../../../modules/receivedResellerAuthorization/types'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import { DiscountType } from '../../../../types/flyout'
import { PlanType, Truncate } from '@labrav/react-components'
import { snakeToSentenceCase, getCurrencyCodeBySymbol } from '@labrav/common'
import { TruncateWithLink } from '../../../../common/components/TruncateWithLink/TruncateWithLink'
import { ColumnReadMore } from '@labrav/common'
import type { Product } from '../../../../modules/productsListing/types'
import {
  agreementDurationTypeViewMapping,
  discountTypeViewMapping,
} from './constants'
import { getContractDurationForEndCustomer } from './helpers'

export {
  agreementDurationTypeViewMapping,
  discountTypeViewMapping,
} from './constants'
export { getContractDurationForEndCustomer } from './helpers'

export const ResellerAuthorizationDetails = ({
  resellerAuthorization,
  product,
  currencySymbol,
}: {
  resellerAuthorization: ViewResellerAuthorizationType
  product?: Product
  currencySymbol?: string
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()
  const resaleAuthorizationOriginType = resellerAuthorization?.agreementOrigin
  const { modifiedDate: createdAtDate, modifiedTime: createdAtTime } =
    resellerAuthorization?.createdAt
      ? productDetailDate(resellerAuthorization?.createdAt, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const { modifiedDate: endDate, modifiedTime: endDateTime } =
    resellerAuthorization?.agreementExpirationDate
      ? productDetailDate(
          resellerAuthorization?.agreementExpirationDate,
          timeZone
        )
      : { modifiedDate: '', modifiedTime: '' }

  const discountPercentage = (
    <DetailInfo
      title={'Discount percentage'}
      value={`${resellerAuthorization?.percentageDiscount}%`}
      keyName={'percentageDiscount'}
    />
  )

  const autoRenewal = (
    <DetailInfo
      title={'Renewal'}
      value={resellerAuthorization?.renewal ? 'Yes' : 'No'}
      keyName={'renewal'}
    />
  )

  const buyerName = (
    <DetailInfo
      title={'Buyer(s)'}
      value={resellerAuthorization?.buyerName || '-'}
      keyName={'buyer'}
    />
  )

  const offerExtendedStatus = (
    <DetailInfo
      title={'Offer extended status'}
      value={resellerAuthorization?.offerExtendedStatus || '-'}
      keyName={'status'}
    />
  )

  const discountType = resellerAuthorization?.discountType?.toUpperCase()

  const isReceivedResellerAuth = resaleAuthorizationOriginType === 'RECEIVED'

  const accountId = isReceivedResellerAuth
    ? resellerAuthorization?.isvCloudAccountId
    : resellerAuthorization?.resellersInfo?.[0]?.resellerAwsAccountId || '-'

  const sellerName = isReceivedResellerAuth
    ? resellerAuthorization?.isvName
    : resellerAuthorization?.resellersInfo?.[0]?.resellerCompanyName || '-'

  const productName = isReceivedResellerAuth
    ? resellerAuthorization.cloudProductName
    : product?.productName

  const producLink = isReceivedResellerAuth
    ? resellerAuthorization?.productId
    : product?.marketplaceUrl

  const productType = isReceivedResellerAuth
    ? resellerAuthorization.productType
    : product?.productType

  const agreementDurationTypeTooltip = isReceivedResellerAuth
    ? 'How long ISV has authorized you to create offers using the specified discounts.'
    : 'How long the specified resellers are authorized to create offers using the specified discounts.'

  const code =
    resellerAuthorization?.currency ||
    getCurrencyCodeBySymbol(currencySymbol || '')

  const netPaymentTerms =
    resellerAuthorization?.netPaymentTerms != null
      ? `${resellerAuthorization.netPaymentTerms} days`
      : '-'

  return (
    <SectionWrapper
      heading="Reseller authorization details"
      icon={faListUl}
      children={
        <div className={classes.section}>
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Authorization name'}
                  value={resellerAuthorization?.agreementName}
                  keyName={'agreementName'}
                />
                <DetailInfo
                  title={'Time length'}
                  value={
                    agreementDurationTypeViewMapping[
                      resellerAuthorization?.agreementDurationType?.toLowerCase()
                    ]
                  }
                  keyName={'agreementDurationType'}
                  infoTooltip={agreementDurationTypeTooltip}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Description'}
                  value={
                    resellerAuthorization?.agreementDescription ? (
                      <ColumnReadMore
                        description={
                          resellerAuthorization?.agreementDescription
                        }
                      />
                    ) : (
                      '-'
                    )
                  }
                  keyName={'agreementDescription'}
                />
                <DetailInfo
                  title={'Buyer name'}
                  value={resellerAuthorization?.companyName || '-'}
                  keyName={'buyerName'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Created on'}
                  value={
                    <div className={classes.dateWrapper}>
                      <div className={classes.dateValue}>{createdAtDate}</div>
                      <div className={classes.timeValue}>{createdAtTime}</div>
                    </div>
                  }
                  keyName={'createdAtDate'}
                />
                <DetailInfo
                  title={'End date'}
                  keyName="agreementExpirationDate"
                  value={
                    resellerAuthorization?.agreementExpirationDate ? (
                      <div className={classes.dateWrapper}>
                        <div className={classes.dateValue}>{endDate}</div>
                        <div className={classes.timeValue}>{endDateTime}</div>
                      </div>
                    ) : (
                      'NA'
                    )
                  }
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={`${isReceivedResellerAuth ? 'Isv' : 'Reseller'} name`}
                  value={sellerName}
                  keyName={'sellerName'}
                />
                <DetailInfo
                  title={`${
                    isReceivedResellerAuth ? 'Isv' : 'Reseller'
                  } AWS account ID`}
                  value={accountId}
                  keyName={'accountId'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Product'}
                  value={
                    productName ? (
                      <>
                        {
                          <TruncateWithLink
                            text={productName}
                            link={producLink || ''}
                          />
                        }
                      </>
                    ) : (
                      '-'
                    )
                  }
                  keyName={'productName'}
                />
                <DetailInfo
                  title={'Product type'}
                  value={productType ? snakeToSentenceCase(productType) : '-'}
                  keyName={'productType'}
                />
              </>
            }
          />
          {productType !== PlanType.SAAS_SUBSCRIPTION && (
            <DetailRow
              borderBottom
              children={
                <>
                  {discountType !== DiscountType.DISCOUNT && (
                    <DetailInfo
                      title={'Duration type'}
                      value={
                        resellerAuthorization?.contractDurationType
                          ? snakeToSentenceCase(
                              resellerAuthorization?.contractDurationType
                            )
                          : 'NA'
                      }
                      keyName={'contractDurationType'}
                      infoTooltip="Which contract durations should be available for Buyer."
                    />
                  )}
                  <DetailInfo
                    title={'Contract duration for end customer'}
                    value={
                      getContractDurationForEndCustomer(
                        resellerAuthorization,
                        isReceivedResellerAuth
                      ) || '-'
                    }
                    infoTooltip="How long the contract will remain valid for the Buyer."
                  />
                </>
              }
            />
          )}
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Maximum allowed service start date'}
                  value={'-'}
                  keyName={'max-allow'}
                />
                <DetailInfo
                  title={'Selling authorization currency'}
                  value={`${code}(${currencySymbol})`}
                  keyName={'currency'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'Discount type'}
                  value={
                    productType === PlanType.SAAS_SUBSCRIPTION
                      ? discountTypeViewMapping[DiscountType.USAGE_PRICING]
                      : discountTypeViewMapping[discountType]
                  }
                  keyName={'discountType'}
                  infoTooltip={
                    'The type of discount provided to Reseller on the selected product(s) for reselling.'
                  }
                />
                {discountType === DiscountType.DISCOUNT
                  ? discountPercentage
                  : autoRenewal}
              </>
            }
          />

          {(discountType === DiscountType.DISCOUNT ||
            isReceivedResellerAuth) && (
            <DetailRow
              borderBottom
              children={
                <>
                  {discountType === DiscountType.DISCOUNT ? autoRenewal : <></>}
                  {isReceivedResellerAuth ? offerExtendedStatus : <></>}
                </>
              }
            />
          )}

          <DetailRow
            borderBottom
            children={
              <>
                {isReceivedResellerAuth && buyerName}
                <DetailInfo
                  title={'Buyer ID'}
                  value={`${resellerAuthorization?.buyerAwsAccountId || '-'}`}
                  keyName={'buyerAwsAccountId'}
                />
              </>
            }
          />
          <DetailRow
            borderBottom
            children={
              <>
                <DetailInfo
                  title={'AWS authorization ID'}
                  value={resellerAuthorization?.awsAgreementId || '-'}
                  keyName={'awsAgreementId'}
                />
                <DetailInfo
                  title={'AWS product ID'}
                  value={
                    <Truncate width={'60%'} clipboardTooltip>
                      {resellerAuthorization?.awsProductId || '-'}
                    </Truncate>
                  }
                  keyName={'awsProductId'}
                />
              </>
            }
          />
          <DetailRow
            children={
              <>
                <DetailInfo
                  title={'Labra authorization ID'}
                  value={
                    <Truncate width={'60%'} clipboardTooltip>
                      {resellerAuthorization?.id || '-'}
                    </Truncate>
                  }
                  keyName={'id'}
                />
                <DetailInfo
                  title={'Net payments terms'}
                  value={netPaymentTerms}
                  keyName={'netPaymentTerms'}
                />
              </>
            }
          />
        </div>
      }
    />
  )
}
